import { JS_FILE_EXTENSION } from "@/app/constants.mjs";
import featureDefaults from "@/app/feature-defaults.mjs";
import { devDebug, devError } from "@/util/dev.mjs";
import { calledByDevtools } from "@/util/devtools.mjs";
import dynamicImport from "@/util/dynamic-import.mjs";
import globals from "@/util/global-whitelist.mjs";
import { camelCaseToKebabCase } from "@/util/helpers.mjs";
import importBasePath from "@/util/import-base-path.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";

// Let's enumerate all feature flags for easier introspection.
// __BLITZ_FEATURES__ should come from the build script.
try {
  /* eslint-disable no-undef */
  for (const name of __BLITZ_FEATURES__) {
    /* eslint-enable no-undef */
    if (!featureDefaults.hasOwnProperty(name)) {
      featureDefaults[name] = false;
    }
  }
} catch (e) {
  devError("FAILED TO INITIALIZE FEATURE FLAGS", e);
}

const obj = makeStrictKeysObject(featureDefaults);

export type FeatureFlags = typeof obj;

const featureFlags = new Proxy(obj, {
  get: restrictRead,
  set: featureFlagHandler,
});

export const featurePromises: { [key: string]: Promise<void> } = {};

const teardowns: { [key: string]: () => void | Promise<void> } = {};

let readSemaphore = false;

/**
 * Allow one read, this is meant to be used as an escape hatch only in
 * some edge cases where reading a feature flag's presence is needed,
 * such as if a feature depends on another feature.
 */
export function allowRead() {
  readSemaphore = true;
  queueMicrotask(() => {
    readSemaphore = false;
  });
}

function restrictRead<T extends object, K extends keyof T>(
  target: T,
  key: K,
  receiver?: unknown,
): T[typeof key] {
  if (typeof key === "string" && !readSemaphore && !calledByDevtools()) {
    throw new Error(
      `Attempted to read "${key}" feature flag without writing. ` +
        `Reading the state of feature flags is banned because application ` +
        `code should not be aware of the existence of feature flags.`,
    );
  }

  return Reflect.get(target, key, receiver);
}

function featureFlagHandler(
  target: FeatureFlags,
  key: keyof FeatureFlags,
  value,
) {
  if (typeof key !== "string") return true;

  const start = Date.now();

  if (typeof value !== "boolean")
    throw new TypeError(
      `Got non-boolean value to toggle feature ${key}: "${value}" 🤡`,
    );

  if (value && !teardowns.hasOwnProperty(key)) {
    // Initialize teardown to something that is valid before the module is loaded.
    teardowns[key] = () => Promise.resolve();

    const importPath = `${importBasePath(
      import.meta.url,
    )}feature-${camelCaseToKebabCase(key)}/mod${JS_FILE_EXTENSION}`;

    const promise = dynamicImport(importPath)
      .then(({ isDeferred, setup, teardown }) => {
        teardowns[key] = teardown;

        // isDeferred is used in case a feature flag relies on other feature flags
        // to initialize first.
        if (isDeferred) {
          return new Promise<void>((resolve, reject) => {
            // This is to ensure that other feature promises are initialized first.
            queueMicrotask(async () => {
              try {
                const promises = Object.keys(featurePromises)
                  .filter((k) => k !== key)
                  .map((k) => featurePromises[k]);
                await Promise.all(promises);
                await setup();
                resolve();
              } catch (error) {
                reject(error);
              }
            });
          });
        }

        return setup();
      })
      .then(() => {
        devDebug(`feature "${key}" ON (${Date.now() - start}ms)`);
      })
      .catch((error) => {
        devError(`feature "${key}" failed to setup`, error);
      });

    featurePromises[key] = promise;
  } else if (!value && teardowns.hasOwnProperty(key)) {
    const teardown = teardowns[key];

    const promise = Promise.resolve()
      .then(() => {
        return teardown();
      })
      .then(() => {
        devDebug(`feature "${key}" OFF (${Date.now() - start}ms)`);
      })
      .catch((error) => {
        devError(`feature "${key}" failed to teardown`, error);
      });

    featurePromises[key] = promise;

    delete teardowns[key];
  }
  return Reflect.set(target, key, value);
}

// Initializing
for (const key in featureFlags) {
  featureFlags[key] = obj[key];
}

globals.__BLITZ_DEV__.featureFlags = featureFlags;

// Don't import this directly, this is exposed under `readState.features`.
// Use actions like `writeState.features.dummy = true` to toggle features.
// See `setFeatureFlag`.
export default featureFlags;

// Utility function to wait for all requested feature flags to setup/teardown.
export function waitForFeatureFlags(flags) {
  const promises = [];
  for (const key in flags) {
    featureFlags[key] = flags[key];
    promises.push(featurePromises[key]);
  }
  return Promise.all(promises);
}
